import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useFragment } from 'react-relay/hooks';

import { IconButton, Theme } from '@material-ui/core';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import TimelineIcon from '@material-ui/icons/Timeline';
import { graphql } from 'babel-plugin-relay/macro';
import classnames from 'classnames';
import { compact, get, isEmpty, map } from 'lodash';

import { ProvisioningEnum } from '@lib/resources/types';

import DialogVideoOnDemandSessionCreation from 'components/DialogVideoOnDemandSessionCreation';
import MenuContainer from 'components/MenuContainer';
import { Avatar, Tooltip } from 'components/v2';
import usePermissions from 'hooks/usePermissions';
import IconLaptop from 'icons/IconLaptop';
import { muiThemeLuma } from 'themes';

import { ActionsPatient_Patient$key } from './__generated__/ActionsPatient_Patient.graphql';
import { ActionsPatient_Viewer$key } from './__generated__/ActionsPatient_Viewer.graphql';
import AvatarChatActivityReassign from './AvatarChatActivityReassign';
import IconButtonPatientActions, { IconButtonPatientActionsProps } from './IconButtonPatientActions';

// TODO extract atoms/fragments from ActionsPatient component
const styles = ({
  custom: {
    palette: { colorSupportPowder },
  },
  spacing: { unit },
}: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      alignItems: 'center',
    },
    hidden: {},
    menu: {
      padding: `0px ${unit}px`,
    },
    journeyIconWrapper: {
      padding: `0 ${unit}px`,
    },
    journeyIconButton: {
      width: unit * 4,
      height: unit * 4,
      padding: 0,
    },
    journeyIcon: {
      fontSize: unit * 2,
    },
    journeyIconSelected: {
      backgroundColor: colorSupportPowder,
    },
    telehealthButtonWrapper: {},
    telehealthButtonIcon: {
      fontSize: 24,
    },
    telehealthDialogContainer: {
      borderRadius: `${unit / 2}px !important`,
      left: -72,
      minWidth: 601,
      overflowY: 'visible',
      padding: 0,
      position: 'relative',
      top: -1 * unit,
    },
    marginRight: {
      marginRight: unit,
    },
    groupMarginRight: {
      marginRight: unit + 12, // group styling difference
    },
  });

const messages = defineMessages({
  videoIconTooltip: {
    id: 'deprecated.fragments.ActionsPatient.videoIconTooltip',
    defaultMessage: 'On demand telehealth and video',
  },
  patientJourneyTooltip: {
    id: 'deprecated.fragments.ActionsPatient.patientJourneyTooltip',
    defaultMessage: 'Patient Journey',
  },
});

const {
  custom: {
    palette: { primary1Color },
  },
} = muiThemeLuma as Theme;

type ActionKey = 'insurance' | 'journey' | 'reassignChatActivity' | 'telehealth';

export interface ActionsPatientProps extends WithStyles<typeof styles> {
  fragmentRefViewer: ActionsPatient_Viewer$key;
  fragmentRef: ActionsPatient_Patient$key;
  actions: ActionKey[];
  moreActions?: IconButtonPatientActionsProps['actions'];
  includePatientJourney?: boolean;
  onAction: (key: string, patient: any) => void;
}

const ActionsPatient: React.FC<ActionsPatientProps> = ({
  classes,
  fragmentRefViewer,
  fragmentRef,
  actions,
  moreActions,
  includePatientJourney,
  onAction,
}) => {
  const { hasAccessToProduct } = usePermissions();
  const viewer = useFragment<ActionsPatient_Viewer$key>(
    graphql`
      fragment ActionsPatient_Viewer on User {
        type
        user {
          setting {
            settings {
              telehealth {
                enabled
              }
            }
          }
        }
        setting {
          settings {
            telehealth {
              enabled
            }
          }
        }
        ...IconButtonPatientActions_Viewer
      }
    `,
    fragmentRefViewer,
  );

  const patient = useFragment<ActionsPatient_Patient$key>(
    graphql`
      fragment ActionsPatient_Patient on Patient
        @argumentDefinitions(includeChatActivity: { type: "Boolean", defaultValue: true }) {
        _id
        contact {
          type
          active
        }
        chatActivity @include(if: $includeChatActivity) {
          _id
          status
          assignee {
            _id
          }
          groupAssignee {
            _id
          }
          ...AvatarChatActivityReassign_ChatActivity
        }
        ...IconButtonPatientActions_Patient @arguments(includeChatActivity: $includeChatActivity)
      }
    `,
    fragmentRef,
  );

  const telehealthPermission = useMemo(() => {
    const patientContacts = get(patient, 'contact', []) || [];

    const getActiveContact = (contactTypes: string[]) => (contact: any) =>
      contact.active && contactTypes.includes(contact.type);
    const getActiveSmsAndEmailContact = getActiveContact(['sms', 'email']);

    const isDoctor = get(viewer, 'type', 'staff') === 'doctor';
    const patientHasActiveSmsOrEmail = !isEmpty(patientContacts.filter(getActiveSmsAndEmailContact));
    const viewerTelehealthEnabled = isDoctor ? get(viewer, 'setting.settings.telehealth.enabled', false) : true;
    const masterTelehealthEnabled = get(viewer, 'user.setting.settings.telehealth.enabled', false);
    const hasTelehealthAccess = hasAccessToProduct(ProvisioningEnum.Telehealth);

    return hasTelehealthAccess && viewerTelehealthEnabled && masterTelehealthEnabled && patientHasActiveSmsOrEmail;
  }, [viewer, patient, hasAccessToProduct]);

  const isGroup = get(patient, 'chatActivity.groupAssignee');
  const { formatMessage } = useIntl();
  const [dialogOnDemandAnchor, setDialogOnDemandAnchor] = useState<any>();

  const elements = useMemo(
    () =>
      compact(
        map(actions, (actionKey) => {
          let element: JSX.Element | undefined;
          switch (actionKey) {
            case 'journey':
              element = (
                <Tooltip
                  key={actionKey}
                  title={formatMessage(messages.patientJourneyTooltip)}
                  placement="top"
                  hideArrow
                >
                  <IconButton
                    className={classnames({
                      [classes.journeyIconSelected]: includePatientJourney,
                    })}
                    aria-label="Journey"
                    aria-haspopup="true"
                    onClick={() => onAction('journey', patient)}
                    data-lh-id="patient-actions-jouney-icon-button"
                  >
                    <TimelineIcon className={classes.journeyIcon} />
                  </IconButton>
                </Tooltip>
              );
              break;
            case 'reassignChatActivity':
              element = (
                <div key={actionKey} className={isGroup ? classes.groupMarginRight : classes.marginRight}>
                  {patient.chatActivity ? (
                    <AvatarChatActivityReassign fragmentRef={patient.chatActivity} size="small" />
                  ) : (
                    <Avatar size="small" />
                  )}
                </div>
              );
              break;
            case 'telehealth':
              if (telehealthPermission) {
                element = (
                  <React.Fragment key={actionKey}>
                    <Tooltip
                      className={classnames({
                        [classes.hidden]: Boolean(dialogOnDemandAnchor),
                      })}
                      title={formatMessage(messages.videoIconTooltip)}
                      placement="top"
                      hideArrow
                    >
                      <IconButton
                        data-lh-id="patient-actions-telehealth-icon-button"
                        onClick={(event) => setDialogOnDemandAnchor(event.currentTarget)}
                      >
                        <IconLaptop className={classes.telehealthButtonIcon} fill={primary1Color} />
                      </IconButton>
                    </Tooltip>
                    <MenuContainer
                      classNameFade={classes.telehealthDialogContainer}
                      onClickAway={() => setDialogOnDemandAnchor(undefined)}
                      menuAnchor={dialogOnDemandAnchor}
                      placement="bottom"
                    >
                      <DialogVideoOnDemandSessionCreation
                        onClose={() => setDialogOnDemandAnchor(undefined)}
                        patient={patient._id}
                      />
                    </MenuContainer>
                  </React.Fragment>
                );
              }
              break;
            default:
              break;
          }
          return element;
        }),
      ),
    [
      actions,
      classes.journeyIconSelected,
      classes.journeyIcon,
      classes.marginRight,
      classes.groupMarginRight,
      classes.hidden,
      classes.telehealthButtonIcon,
      classes.telehealthDialogContainer,
      includePatientJourney,
      isGroup,
      patient,
      telehealthPermission,
      onAction,
      dialogOnDemandAnchor,
      formatMessage,
    ],
  );

  return (
    <div className={classes.root}>
      {elements}
      {moreActions && (
        <IconButtonPatientActions
          fragmentRefViewer={viewer}
          fragmentRef={patient}
          onAction={onAction}
          actions={moreActions}
        />
      )}
    </div>
  );
};

export default withStyles(styles)(ActionsPatient);
